/**
 * ColumnPicker - the sheet's column chooser (docs/plans/crm-recruitment-list-plan.md Part A section
 * 4, B1, B4): show, hide and reorder the columns, per person, per study, on this device (pref
 * crm.recruit.columns.<studyId>, no server-side saved views: section 7). Name (with the tick box)
 * always shows and always comes first. The study's answer columns join the list as soon as an admin
 * picks them, in their default place, without a reset. The model is recruitLogic's columnChoices /
 * toggleColumn / moveColumn; this file is the popover and the pref.
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, SlidersHorizontal } from 'lucide-react';
import { outlineBtnCls } from '../components/filters';
import { Z } from '../components/ui';
import { getPref, setPref } from '../lib';
import { columnChoices, moveColumn, parseColumnPrefs, serializeColumnPrefs, toggleColumn } from './recruitLogic';
import type { RecruitAnswerColumn, SheetColumnPrefs } from './types';

/** The pref key (under lib's `crm.` prefix): crm.recruit.columns.<studyId>. */
export const columnPrefsKey = (studyId: string): string => `recruit.columns.${studyId}`;

/** This person's column choice for the study on this device, or null for the default. */
export function readColumnPrefs(studyId: string): SheetColumnPrefs | null {
  return parseColumnPrefs(getPref(columnPrefsKey(studyId), ''));
}

/** Remember (or, with null, forget) the column choice for the study on this device. */
export function writeColumnPrefs(studyId: string, prefs: SheetColumnPrefs | null): void {
  setPref(columnPrefsKey(studyId), prefs ? serializeColumnPrefs(prefs) : '');
}

export interface ColumnPickerProps {
  /** The current choice (null = the default). */
  prefs: SheetColumnPrefs | null;
  /** The study's answer columns (at most six). */
  answerColumns: readonly RecruitAnswerColumn[];
  /** A new choice; null = back to the default. */
  onChange: (next: SheetColumnPrefs | null) => void;
  className?: string;
}

const moveBtnCls =
  'inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-30 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-7 sm:w-7';

export function ColumnPicker({ prefs, answerColumns, onChange, className = '' }: ColumnPickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Outside click closes (mousedown, so the click still lands where it was aimed).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // The tick box rides with Name and is not a column anyone reads: it is left out of the list.
  const choices = columnChoices(prefs, answerColumns).filter((c) => c.key !== 'select');
  const hiddenCount = choices.filter((c) => !c.visible).length;
  const firstMovable = choices.findIndex((c) => c.hideable);

  return (
    <div
      ref={rootRef}
      className={`relative ${className}`}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Show, hide and order the columns"
        className={outlineBtnCls(hiddenCount > 0)}
      >
        <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
        Columns{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
      </button>
      {open && (
        <div
          aria-label="Choose the columns"
          className={`absolute right-0 top-full mt-1 w-72 max-w-[calc(100vw-2rem)] ${Z.menu} rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg dark:border-gray-700 dark:bg-gray-800`}
        >
          <div className="flex items-center justify-between gap-2 px-2 pb-1 pt-1">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Columns on this device
            </span>
            <button
              type="button"
              onClick={() => onChange(null)}
              disabled={!prefs}
              className="inline-flex min-h-[44px] items-center rounded-md px-2 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-40 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:h-7 sm:min-h-0"
            >
              Reset
            </button>
          </div>
          <ul className="max-h-80 overflow-y-auto">
            {choices.map((c, i) => (
              <li key={c.key} className="flex min-h-[44px] items-center gap-2 rounded-lg px-2 sm:min-h-0 sm:py-1">
                <input
                  id={`recruit-col-${c.key}`}
                  type="checkbox"
                  checked={c.visible}
                  disabled={!c.hideable}
                  onChange={() => onChange(toggleColumn(prefs, c.key))}
                  className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700"
                />
                <label
                  htmlFor={`recruit-col-${c.key}`}
                  className={`min-w-0 flex-1 truncate text-sm ${c.hideable ? 'cursor-pointer text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}
                >
                  {c.label}
                  {!c.hideable && <span className="ml-1 text-[11px]">(always shown)</span>}
                </label>
                <button
                  type="button"
                  onClick={() => onChange(moveColumn(prefs, c.key, -1, answerColumns))}
                  disabled={!c.hideable || i <= firstMovable}
                  aria-label={`Move ${c.label} left`}
                  title="Move left"
                  className={moveBtnCls}
                >
                  <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(moveColumn(prefs, c.key, 1, answerColumns))}
                  disabled={!c.hideable || i === choices.length - 1}
                  aria-label={`Move ${c.label} right`}
                  title="Move right"
                  className={moveBtnCls}
                >
                  <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ColumnPicker;
